// mirror of auware/.../frontend/src/components/ui/Textarea.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type TextareaHTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label displayed above the textarea. */
  label?: string;
  /** Error message displayed below the textarea. */
  error?: string;
  /** Helper text displayed below the textarea (hidden when `error` is set). */
  helperText?: string;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const baseTextareaClasses =
  "mt-1 block w-full rounded-lg border-neutral-300 shadow-sm " +
  "focus:border-primary-500 focus:ring-primary-500 sm:text-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

const errorTextareaClasses =
  "mt-1 block w-full rounded-lg border-red-500 shadow-sm " +
  "focus:border-red-500 focus:ring-red-500 sm:text-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Multi-line text input with optional label, error state, and helper text.
 *
 * ```tsx
 * <Textarea label="Description" rows={6} error={errors.description} />
 * ```
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className = "", label, error, helperText, id, rows = 4, ...props },
    ref,
  ) => {
    return (
      <div>
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-neutral-700"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          className={`${error ? errorTextareaClasses : baseTextareaClasses} ${className}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? `${id}-error` : helperText ? `${id}-helper` : undefined
          }
          {...props}
        />
        {error && (
          <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${id}-helper`} className="mt-1 text-xs text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
